/* Native Shopify cart, variant and dialog behavior. No source app code or tracking. */
(() => {
  if (window.northNativeInitialized) return;
  window.northNativeInitialized = true;
  const root = document.body.dataset.root || '/';
  const status = message => { document.getElementById('NativeStatus').textContent = message; };
  const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency', currency: document.body.dataset.currency || 'USD'
  }).format(cents / 100);
  let restoreFocus;
  let cartCloseRevision = 0;
  let cartOverflow;
  const headerControllers = new Map();
  function closeMenuDrawer(details) {
    if (!details) return;
    details.querySelectorAll('details[open]').forEach(child => { child.open = false; child.classList.remove('menu-opening'); });
    details.querySelectorAll('.submenu-open').forEach(child => child.classList.remove('submenu-open'));
    details.open = false;
    details.classList.remove('menu-opening');
    details.closest('.section-header')?.classList.remove('menu-open');
    const summary = details.querySelector(':scope > summary');
    summary?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = document.querySelector('dialog[open],modal-dialog[open],details-modal > details[open],menu-drawer > details[open],header-drawer > details[open]') ? 'hidden' : '';
    summary?.focus();
  }
  function initializeHeaders(container) {
    container.querySelectorAll('sticky-header').forEach(host => {
      if (headerControllers.has(host)) return;
      const header = host.closest('.section-header');
      if (!header) return;
      const always = ['always','reduce-logo-size'].includes(host.dataset.stickyType);
      let previous = window.scrollY || 0;
      let bounds;
      const measure = () => {
        document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
        // Source uses 990px for header sizing; remeasure every resize, including that boundary.
        // Keep the initial flow anchor; a sticky/hidden header rect moves with the viewport.
        const top = bounds?.top ?? header.getBoundingClientRect().top + (window.scrollY || 0);
        bounds = {top, bottom:top + header.offsetHeight};
      };
      const cleanup = () => {
        window.removeEventListener('scroll', scroll);
        window.removeEventListener('resize', measure);
        headerControllers.delete(host);
      };
      const scroll = () => {
        if (!host.isConnected) { cleanup(); return; }
        const current = window.scrollY || 0;
        const menuOpen = host.querySelector('header-drawer > details[open],menu-drawer > details[open]');
        const searchOpen = host.querySelector('details-modal > details[open]');
        if (menuOpen || searchOpen) { previous = current; return; }
        if (current > bounds.bottom) {
          header.classList.add('scrolled-past-header');
          if (!always && current !== previous) {
            header.classList.add('shopify-section-header-sticky');
            if (current > previous) {
              header.classList.add('shopify-section-header-hidden');
              header.classList.remove('animate');
            } else {
              header.classList.remove('shopify-section-header-hidden');
              header.classList.add('animate');
            }
          }
        } else if (current <= bounds.top) {
          header.classList.remove('scrolled-past-header');
          if (!always) header.classList.remove('shopify-section-header-hidden','shopify-section-header-sticky','animate');
        }
        previous = current;
      };
      measure();
      if (always) header.classList.add('shopify-section-header-sticky');
      headerControllers.set(host, cleanup);
      window.addEventListener('scroll', scroll, {passive:true});
      window.addEventListener('resize', measure);
    });
  }
  let cartRevision = 0;
  function playVideo(video) {
    video.play().catch(() => {
      const overlay = video.parentElement.querySelector('.gp-thumbnail-video');
      if (overlay) overlay.style.display = '';
      const button = video.parentElement.querySelector('.internal-video__play,.gp-thumbnail-video button');
      if (button) { button.style.visibility = 'visible'; button.removeAttribute('aria-hidden'); button.removeAttribute('tabindex'); }
    });
  }
  const videoObserver = new IntersectionObserver(entries => entries.forEach(entry => {
    if (entry.isIntersecting) { playVideo(entry.target); videoObserver.unobserve(entry.target); }
  }));
  function initializeVideos(container) {
    container.querySelectorAll('internal-video video,gp-lite-html5-embed video').forEach(video => {
      if (video.dataset.nativeVideoInitialized) return;
      video.dataset.nativeVideoInitialized = 'true';
      video.muted = video.hasAttribute('muted');
      const wrapper = video.parentElement;
      const button = wrapper.querySelector('.internal-video__play,.gp-thumbnail-video button');
      const update = () => {
        wrapper.classList.toggle('internal-video--playing', !video.paused);
        button?.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
        const overlay = wrapper.querySelector('.gp-thumbnail-video');
        if (overlay && !video.paused) overlay.style.display = 'none';
      };
      video.addEventListener('play', update); video.addEventListener('pause', update); update();
      if (wrapper.dataset.noPlayBtn === 'true' && button) { button.setAttribute('aria-hidden', 'true'); button.tabIndex = -1; }
      if (wrapper.dataset.autoplay === 'true' || video.hasAttribute('autoplay')) videoObserver.observe(video);
    });
  }
  function openDialog(dialog, trigger) {
    if (!dialog) return;
    if (trigger || !restoreFocus?.isConnected) restoreFocus = trigger || document.activeElement;
    if (dialog.id === 'NativeCart' && cartOverflow === undefined) cartOverflow = document.body.style.overflow;
    if (dialog.tagName === 'DIALOG') dialog.showModal();
    else { dialog.setAttribute('open', ''); dialog.querySelector('[role=dialog]')?.focus(); }
    document.body.style.overflow = 'hidden';
  }
  function closeDialog(dialog) {
    if (!dialog) return;
    if (dialog.tagName === 'DIALOG') dialog.close();
    else dialog.removeAttribute('open');
    if (dialog.id === 'NativeCart') {
      if (cartOverflow !== undefined) {
        cartCloseRevision++;
        document.body.style.overflow = cartOverflow;
        cartOverflow = undefined;
        restoreFocus?.focus();
      }
      return;
    }
    document.body.style.overflow = '';
    restoreFocus?.focus();
  }
  function openImageZoom(image, trigger) {
    if (!image) return;
    const dialog = document.getElementById('NativeMediaZoom');
    const preview = dialog.querySelector('img');
    const url = new URL(image.src, location.href);
    preview.sizes = '(min-width: 750px) calc(100vw - 22rem), 1100px';
    preview.srcset = [550,1100,1445].map(width => { url.searchParams.set('width', width); return url.href + ' ' + width + 'w'; }).join(',');
    preview.src = url.href;
    preview.alt = image.alt;
    openDialog(dialog, trigger);
  }
  function updateCartCount(section) {
    const count = Number(section?.dataset.cartCount || 0);
    document.querySelectorAll('#cart-icon-bubble').forEach(icon => {
      let bubble = icon.querySelector('.cart-count-bubble');
      if (!count) { bubble?.remove(); return; }
      if (!bubble) { bubble = document.createElement('div'); bubble.className = 'cart-count-bubble'; icon.append(bubble); }
      bubble.textContent = count;
      bubble.setAttribute('aria-label', `${count} ${count === 1 ? 'item' : 'items'}`);
    });
  }
  function replaceCartSection(markup) {
    if (!markup) throw new Error('Cart view could not reload. Open the cart page to review it.');
    const html = new DOMParser().parseFromString(markup, 'text/html');
    const current = document.getElementById('NativeCartSection');
    const next = html.getElementById('NativeCartSection');
    if (!current || !next) throw new Error('Cart section is unavailable.');
    current.replaceWith(next);
    updateCartCount(next);
  }
  async function cartRequest(path, body) {
    cartRevision++;
    const response = await fetch(root + path, {method:'POST', headers:{'Content-Type':'application/json', 'Accept':'application/json'},
      body:JSON.stringify({...body, sections:['cart-panel'], sections_url:location.pathname})});
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || 'Cart could not be updated.');
    replaceCartSection(result.sections?.['cart-panel']);
    return result;
  }
  async function refreshCart() {
    const revision = cartRevision;
    const response = await fetch(root + 'cart?sections=cart-panel', {cache:'no-store'});
    if (!response.ok) throw new Error('Cart could not be refreshed.');
    const sections = await response.json();
    if (revision === cartRevision) replaceCartSection(sections['cart-panel']);
  }
  function productFor(form) {
    const section = form.closest('[data-native-product-root]');
    const script = section?.querySelector('[data-native-product]');
    return script ? JSON.parse(script.textContent) : null;
  }
  function selectedVariants(form, product) {
    const rows = [...form.querySelectorAll('[data-unit]')];
    if (!rows.length) {
      const id = form.elements?.namedItem('id') || form.querySelector('[name=id]');
      const quantityControl = form.elements?.namedItem('quantity') || form.querySelector('[name=quantity]');
      const quantity = quantityControl ? (quantityControl.value.trim() ? Number(quantityControl.value) : NaN) : 1;
      const variant = product?.variants.find(variant => String(variant.id) === String(id?.value));
      return Number.isSafeInteger(quantity) && quantity > 0 ? [variant] : [];
    }
    const units = Number(form.querySelector('[name=native_offer]:checked')?.value || 1);
    return rows.slice(0, units).map(row => {
      const options = [...row.querySelectorAll('[data-option]')].map(select => select.value);
      return product.variants.find(variant => variant.options.every((value,index) => value === options[index]));
    });
  }
  function renderReviews(widget, reset = false) {
    const cards = [...widget.querySelectorAll('.jdgm-review-list > [data-native-review]')];
    const sort = widget.querySelector('.jdgm-sort-dropdown')?.value || 'created_at';
    const rating = Number(widget.dataset.reviewRating || 0);
    const orders = JSON.parse(widget.querySelector('[data-native-review-orders]')?.textContent || '{}');
    const order = orders[sort] || [];
    const pictures = card => !!card.querySelector('.jm-media-thumbnail-list__thumbnail img');
    const video = card => !!card.querySelector('video,[aria-label="View review video"]');
    const rank = card => { const index = order.indexOf(card.dataset.nativeReview); return index < 0 ? Infinity : index; };
    cards.sort((first, second) => {
      const observed = rank(first) - rank(second);
      if (!Number.isNaN(observed) && observed) return observed;
      const score = Number(second.dataset.reviewRating) - Number(first.dataset.reviewRating);
      if (sort === 'highest_rating' && score) return score;
      if (sort === 'lowest_rating' && score) return -score;
      if (sort === 'pictures_first' && pictures(first) !== pictures(second)) return Number(pictures(second)) - Number(pictures(first));
      if (sort === 'videos_first' && video(first) !== video(second)) return Number(video(second)) - Number(video(first));
      return second.dataset.reviewDate.localeCompare(first.dataset.reviewDate);
    });
    if (reset) widget.dataset.reviewLimit = widget.dataset.initialReviewLimit || '4';
    const limit = Number(widget.dataset.reviewLimit || 4);
    let matched = 0;
    const listing = widget.querySelector('.jdgm-review-list');
    cards.forEach(card => {
      const matches = (!rating || Number(card.dataset.reviewRating) === rating) && (sort !== 'with_pictures' || pictures(card));
      card.hidden = !matches || matched++ >= limit;
      listing.append(card);
    });
    const more = widget.querySelector('[aria-label="Show more reviews"]');
    if (more) more.hidden = matched <= limit;
    widget.querySelector('[data-review-status]').textContent = `${Math.min(limit, matched)} of ${matched} available archived reviews. New reviews are sent to this store for manual publication.`;
    widget.querySelectorAll('[data-review-filter-rating]').forEach(input => {
      input.checked = Number(input.value) === rating;
      input.closest('label')?.classList.toggle('jm-rating-filter__tag--active', input.checked);
    });
    const clear = widget.querySelector('[data-review-clear]');
    if (clear) { clear.classList.toggle('jm-filter-panel__clear-btn--hidden', !rating); clear.setAttribute('aria-hidden', String(!rating)); clear.tabIndex = rating ? 0 : -1; }
    widget.querySelectorAll('.jm-histogram-row').forEach(row => row.setAttribute('aria-pressed', String(Number(row.querySelector('.jm-histogram-row__rating')?.textContent) === rating)));
  }
  function selectReviewImage(dialog, index) {
    const images = dialog.reviewImages || [];
    if (!images.length) return;
    index = Math.max(0, Math.min(index, images.length - 1));
    dialog.dataset.reviewImage = index;
    const image = dialog.querySelector('figure img');
    image.src = images[index].src;
    image.alt = 'Original-store review image';
    dialog.querySelector('figure').style.setProperty('--168bd92f', `url(${JSON.stringify(images[index].src)}) lightgray 50% / cover no-repeat`);
    dialog.querySelector('[data-review-previous]').disabled = index === 0;
    dialog.querySelector('[data-review-next]').disabled = index === images.length - 1;
    dialog.querySelectorAll('[data-review-image-index]').forEach(button => {
      const active = Number(button.dataset.reviewImageIndex) === index;
      button.setAttribute('aria-pressed', String(active));
      button.classList.toggle('jm-media-grid__item--active', active);
      button.tabIndex = active ? 0 : -1;
    });
  }
  function openReviewForm(target) {
    const widget = target.closest('[data-native-reviews]');
    const dialog = document.getElementById('NativeWriteReview');
    const id = widget?.dataset.reviewProduct || '';
    const title = widget?.dataset.reviewTitle?.trim();
    if (!dialog || !/^\d+$/.test(id) || !Number.isSafeInteger(Number(id)) || Number(id) <= 0 || !title) {
      status('This product could not be selected. Reload the page to write a review.');
      return;
    }
    const product = dialog.querySelector('[data-native-review-product]');
    const productId = dialog.querySelector('[data-native-review-product-id]');
    if (product && productId) { product.value = title; productId.value = id; }
    dialog.querySelector('[data-native-review-product-title]').textContent = title;
    openDialog(dialog, target);
    dialog.querySelector('[data-native-review-result]')?.focus();
  }
  function reviewClick(target) {
    if (target.matches('button[data-native-write-review]')) { openReviewForm(target); return true; }
    const media = target.closest('#NativeReviewMedia');
    if (media && target.matches('[data-review-previous],[data-review-next],[data-review-image-index]')) {
      const index = target.hasAttribute('data-review-image-index') ? Number(target.dataset.reviewImageIndex) : Number(media.dataset.reviewImage || 0) + (target.hasAttribute('data-review-next') ? 1 : -1);
      selectReviewImage(media, index);
      return true;
    }
    if (target.matches('.jm-review-content__expand-button') && target.closest('[data-native-review]')) {
      target.closest('.jm-review-content').querySelector('.jm-review-content__body').classList.add('native-review-expanded');
      target.hidden = true;
      return true;
    }
    const widget = target.closest('[data-native-reviews]');
    if (!widget) return false;
    if (target.matches('[aria-label="Show more reviews"]')) {
      widget.dataset.reviewLimit = Number(widget.dataset.reviewLimit || 4) + Number(widget.dataset.initialReviewLimit || 4);
      renderReviews(widget);
    } else if (target.matches('.jm-custom-answer-filter')) {
      const panel = widget.querySelector('[data-review-filters]');
      if (!panel) return false;
      panel.hidden = !panel.hidden;
      target.setAttribute('aria-expanded', String(!panel.hidden));
      target.classList.toggle('jm-custom-answer-filter--active', !panel.hidden);
    } else if (target.matches('.jm-histogram-row,[data-review-clear]')) {
      widget.dataset.reviewRating = target.hasAttribute('data-review-clear') ? '0' : target.querySelector('.jm-histogram-row__rating').textContent.trim();
      renderReviews(widget, true);
    } else if (target.matches('.jm-media-grid__arrow')) {
      const strip = target.closest('.jm-media-grid').querySelector('.jm-media-grid__container');
      strip.scrollBy({left:strip.clientWidth * (target.classList.contains('jm-media-grid__arrow--next') ? 1 : -1), behavior:'smooth'});
    } else if (target.matches('.jm-media-grid__item,.jm-media-thumbnail-list__thumbnail')) {
      const clicked = target.querySelector('img');
      const card = target.closest('[data-native-review]') || [...widget.querySelectorAll('[data-native-review]')].find(card => [...card.querySelectorAll('.jm-media-thumbnail-list__thumbnail img')].some(img => img.src.split('?')[0] === clicked?.src.split('?')[0]));
      if (!card || !clicked) return false;
      const dialog = document.getElementById('NativeReviewMedia');
      const thumbs = [...card.querySelectorAll('.jm-media-thumbnail-list__thumbnail img')];
      dialog.reviewImages = thumbs.map(img => ({src:img.dataset.reviewFullSrc || img.src, thumbnail:img.src}));
      const old = dialog.querySelector('.jm-review-item');
      const copy = card.cloneNode(true);
      copy.hidden = false;
      copy.querySelectorAll('.jm-media-thumbnail-list__thumbnail').forEach(thumb => thumb.closest('.jm-cluster')?.remove());
      old.replaceWith(copy);
      const strip = dialog.querySelector('.jm-media-grid__container');
        strip.setAttribute('aria-label', `Thumbnails for ${dialog.reviewImages.length} items`);
      const template = strip.querySelector('button').cloneNode(true);
      strip.replaceChildren(...dialog.reviewImages.map((image, index) => {
        const button = template.cloneNode(true);
        button.dataset.reviewImageIndex = index;
        button.setAttribute('aria-label', `Go to item ${index + 1}`);
        button.querySelector('img').src = image.thumbnail;
        button.querySelector('img').removeAttribute('srcset');
        button.querySelector('img').alt = `Thumbnail ${index + 1}`;
        return button;
      }));
      selectReviewImage(dialog, Math.max(0, thumbs.indexOf(clicked)));
      openDialog(dialog, target);
    } else return false;
    return true;
  }
  function initializeFacetFilters(container) {
    container.querySelectorAll('form[data-native-filters]').forEach(form => {
      if (form.nativeFiltersInitialized) return;
      form.nativeFiltersInitialized = true;
      const submit = () => { if (form.dataset.nativeFilters === 'desktop') form.requestSubmit(); };
      form.addEventListener('change', submit);
      form.querySelectorAll('[data-native-price-range]').forEach(range => {
        const fields = [...range.querySelectorAll('input[type=number]')];
        const handles = [...range.querySelectorAll('[data-handle]')];
        const slider = range.querySelector('.price-slider');
        const maximum = Number(range.dataset.max);
        const value = side => fields[side === 'min' ? 0 : 1].value === '' ? (side === 'min' ? 0 : maximum) : Number(fields[side === 'min' ? 0 : 1].value);
        const paint = () => {
          const low = value('min'), high = value('max');
          fields[0].max = high; fields[1].min = low;
          handles.forEach(handle => {
            const current = value(handle.dataset.handle);
            handle.style.left = (maximum ? current / maximum * 100 : 0) + '%';
            handle.setAttribute('role', 'slider');
            handle.setAttribute('aria-valuemin', handle.dataset.handle === 'min' ? 0 : low);
            handle.setAttribute('aria-valuemax', handle.dataset.handle === 'min' ? high : maximum);
            handle.setAttribute('aria-valuenow', current);
            handle.setAttribute('aria-valuetext', money(current * 100));
            range.querySelector('[data-label="' + handle.dataset.handle + '"]').textContent = new Intl.NumberFormat(document.documentElement.lang, {maximumFractionDigits:2}).format(current);
          });
          const fill = range.querySelector('.price-slider__range');
          fill.style.left = (maximum ? low / maximum * 100 : 0) + '%';
          fill.style.right = (maximum ? 100 - high / maximum * 100 : 0) + '%';
        };
        const set = (side, amount) => {
          const low = side === 'min' ? 0 : value('min');
          const high = side === 'min' ? value('max') : maximum;
          fields[side === 'min' ? 0 : 1].value = Math.round(Math.max(low, Math.min(amount, high)) * 100) / 100;
          paint();
        };
        fields.forEach((field, index) => field.addEventListener('change', () => {
          if (field.value !== '') set(index ? 'max' : 'min', Number(field.value));
          else paint();
        }));
        handles.forEach(handle => {
          let dragging = false;
          const move = event => {
            const box = slider.getBoundingClientRect();
            if (box.width) set(handle.dataset.handle, (event.clientX - box.left) / box.width * maximum);
          };
          handle.style.touchAction = 'none';
          handle.addEventListener('pointerdown', event => {
            if (event.button > 0) return;
            event.preventDefault(); dragging = true; handle.setPointerCapture(event.pointerId);
          });
          handle.addEventListener('pointermove', event => { if (dragging) move(event); });
          handle.addEventListener('pointerup', event => { if (dragging) { move(event); dragging = false; submit(); } });
          handle.addEventListener('pointercancel', () => { dragging = false; });
          handle.addEventListener('keydown', event => {
            const deltas = {ArrowLeft:-1,ArrowDown:-1,ArrowRight:1,ArrowUp:1,PageDown:-10,PageUp:10};
            if (!(event.key in deltas) && event.key !== 'Home' && event.key !== 'End') return;
            event.preventDefault();
            set(handle.dataset.handle, event.key === 'Home' ? 0 : event.key === 'End' ? maximum : value(handle.dataset.handle) + deltas[event.key]);
            submit();
          });
        });
        paint();
      });
      const close = form.closest('menu-drawer')?.querySelector('[data-native-facets-close]');
      if (close) {
        const dismiss = () => closeMenuDrawer(form.closest('menu-drawer').querySelector(':scope > details'));
        close.addEventListener('click', event => { event.preventDefault(); dismiss(); });
        close.addEventListener('keydown', event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); dismiss(); } });
      }
    });
  }
  function initializePredictiveSearch(container) {
    container.querySelectorAll('predictive-search').forEach(host => {
      if (host.nativeSearch) return;
      const input = host.querySelector('input[name=q]');
      const form = host.querySelector('form');
      const panel = host.querySelector('[data-predictive-search]');
      const reset = host.querySelector('[type=reset]');
      const live = host.querySelector('.predictive-search-status');
      if (!input || !form || !panel) return;
      const loadingMarkup = panel.querySelector('.predictive-search__loading-state')?.outerHTML || '';
      let revision = 0, timer, controller;
      const announce = text => { if (live) { live.textContent = text; live.setAttribute('aria-hidden','false'); } };
      const close = () => {
        revision++; clearTimeout(timer); controller?.abort();
        host.removeAttribute('open'); host.removeAttribute('loading');
        input.setAttribute('aria-expanded','false'); input.removeAttribute('aria-activedescendant');
        panel.querySelectorAll('[aria-selected=true]').forEach(option => option.setAttribute('aria-selected','false'));
      };
      const change = () => {
        close();
        const term = input.value.trim(), version = revision;
        if (reset) { reset.hidden = !term; reset.classList.toggle('hidden', !term); }
        if (!term) { panel.innerHTML = loadingMarkup; announce(''); return; }
        timer = setTimeout(async () => {
          controller = new AbortController(); host.setAttribute('loading','');
          announce(host.dataset.loadingText || 'Loading...');
          try {
            const params = new URLSearchParams({q:term, section_id:'predictive-search'});
            const preview = new URL(location.href).searchParams.get('preview_theme_id');
            if (preview && /^\d+$/.test(preview)) params.set('preview_theme_id', preview);
            const response = await fetch(root + 'search/suggest?' + params, {signal:controller.signal, credentials:'same-origin'});
            if (!response.ok) throw new Error('Search suggestions are unavailable. Submit search to view results.');
            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            if (version !== revision || term !== input.value.trim()) return;
            const section = html.getElementById('shopify-section-predictive-search');
            if (!section?.querySelector('#predictive-search-results')) throw new Error('Search suggestions are unavailable. Submit search to view results.');
            panel.innerHTML = section.innerHTML;
            host.setAttribute('open',''); input.setAttribute('aria-expanded','true');
            announce(panel.querySelector('[data-predictive-search-live-region-count-value]')?.textContent.trim() || 'Search suggestions loaded.');
          } catch(error) {
            if (version !== revision || error.name === 'AbortError') return;
            close(); announce('Search suggestions are unavailable. Submit search to view results.');
          } finally { if (version === revision) host.removeAttribute('loading'); }
        }, 300);
      };
      const keydown = event => {
        if (event.target !== input || !host.hasAttribute('open')) return;
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); input.focus(); return; }
        const options = [...panel.querySelectorAll('[role=option]')].filter(option => option.getClientRects().length && getComputedStyle(option).visibility !== 'hidden');
        const selected = options.findIndex(option => option.getAttribute('aria-selected') === 'true');
        if (event.key === 'Enter' && selected >= 0) {
          event.preventDefault(); const option = options[selected];
          (option.matches('a,button') ? option : option.querySelector('a,button'))?.click();
        } else if (['ArrowDown','ArrowUp'].includes(event.key) && options.length) {
          event.preventDefault();
          const index = selected < 0 ? (event.key === 'ArrowDown' ? 0 : options.length - 1) : (selected + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
          panel.querySelectorAll('[role=option]').forEach(option => option.setAttribute('aria-selected','false'));
          const option = options[index];
          if (!option.id) option.id = input.id + '-option-' + index;
          option.setAttribute('aria-selected','true'); input.setAttribute('aria-activedescendant', option.id);
          option.scrollIntoView({block:'nearest'});
        }
      };
      const clear = event => { event.preventDefault(); input.value = ''; change(); input.focus(); };
      const blur = () => queueMicrotask(() => { if (!host.contains(document.activeElement)) close(); });
      input.setAttribute('role','combobox'); input.setAttribute('aria-autocomplete','list');
      input.setAttribute('aria-controls','predictive-search-results'); input.setAttribute('aria-haspopup','listbox');
      input.setAttribute('aria-expanded','false');
      if (reset) { reset.hidden = !input.value.trim(); reset.classList.toggle('hidden', reset.hidden); }
      input.addEventListener('input', change); input.addEventListener('focus', change);
      host.addEventListener('keydown', keydown); host.addEventListener('focusout', blur); form.addEventListener('reset', clear);
      host.nativeSearch = {cleanup() {
        close(); input.removeEventListener('input', change); input.removeEventListener('focus', change);
        host.removeEventListener('keydown', keydown); host.removeEventListener('focusout', blur); form.removeEventListener('reset', clear);
        delete host.nativeSearch;
      }};
    });
  }
  function initializeLandingGalleries(container) {
    container.querySelectorAll('.lps-buy-media').forEach(gallery => {
      if (gallery.nativeLandingGallery) return;
      const track = gallery.querySelector('.lps-slides');
      const slides = [...gallery.querySelectorAll('.lps-slide')];
      const strip = gallery.querySelector('.lps-thumbs');
      const thumbs = [...gallery.querySelectorAll('.lps-thumb')];
      if (!track || !slides.length) return;
      let index = 0, snapTimer, syncTimer;
      const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const mark = value => {
        index = Math.max(0, Math.min(value, slides.length - 1));
        gallery.dataset.nativeLandingIndex = index;
        thumbs.forEach((thumb, position) => {
          thumb.classList.toggle('on', position === index);
          thumb.setAttribute('aria-pressed', String(position === index));
        });
        const active = thumbs[index];
        if (active && strip) {
          const bounds = active.getBoundingClientRect(), visible = strip.getBoundingClientRect();
          if (bounds.left < visible.left || bounds.right > visible.right) strip.scrollTo({left:strip.scrollLeft + bounds.left - visible.left - 8, behavior:reducedMotion() ? 'auto' : 'smooth'});
        }
      };
      const select = (value, smooth = true) => {
        if (!track.clientWidth) return;
        const target = Math.max(0, Math.min(value, slides.length - 1)) * track.clientWidth;
        track.style.scrollSnapType = 'none';
        track.scrollTo({left:target, behavior:smooth && !reducedMotion() ? 'smooth' : 'auto'});
        clearTimeout(snapTimer);
        snapTimer = setTimeout(() => {
          track.style.scrollSnapType = '';
          if (Math.abs(track.scrollLeft - target) > 4) track.scrollTo({left:target, behavior:'auto'});
        }, smooth && !reducedMotion() ? 550 : 0);
        mark(Math.round(target / track.clientWidth));
      };
      const click = event => {
        const arrow = event.target.closest('.lps-prev,.lps-next');
        const thumb = event.target.closest('.lps-thumb');
        if (arrow && gallery.contains(arrow)) { event.preventDefault(); select(Math.round(track.scrollLeft / track.clientWidth) + (arrow.classList.contains('lps-prev') ? -1 : 1)); }
        else if (thumb && gallery.contains(thumb)) { event.preventDefault(); select(thumbs.indexOf(thumb)); }
      };
      const keydown = event => {
        const thumb = event.target.closest('.lps-thumb');
        if (!thumb || !gallery.contains(thumb)) return;
        const current = thumbs.indexOf(thumb);
        const value = event.key === 'ArrowLeft' ? current - 1 : event.key === 'ArrowRight' ? current + 1 : event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : current;
        if (!['Enter',' ','ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
        event.preventDefault(); select(value);
        thumbs[index]?.focus();
      };
      const sync = () => {
        if (syncTimer) return;
        syncTimer = setTimeout(() => { syncTimer = null; if (track.clientWidth) mark(Math.round(track.scrollLeft / track.clientWidth)); }, 120);
      };
      const resize = () => select(index, false);
      thumbs.forEach(thumb => { thumb.setAttribute('role','button'); thumb.tabIndex = 0; });
      gallery.addEventListener('click', click);
      gallery.addEventListener('keydown', keydown);
      track.addEventListener('scroll', sync, {passive:true});
      window.addEventListener('resize', resize);
      gallery.nativeLandingGallery = {cleanup() {
        gallery.removeEventListener('click', click); gallery.removeEventListener('keydown', keydown);
        track.removeEventListener('scroll', sync); window.removeEventListener('resize', resize);
        clearTimeout(snapTimer); clearTimeout(syncTimer); track.style.scrollSnapType = '';
        delete gallery.nativeLandingGallery;
      }};
      mark(track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0);
    });
  }
  function initializeScrollTop(container) {
    container.querySelectorAll('[data-native-scroll-top]').forEach(button => {
      if (button.nativeScrollTop) return;
      const update = () => { button.hidden = window.scrollY <= 400; };
      const click = () => window.scrollTo({top:0, behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
      window.addEventListener('scroll', update, {passive:true}); button.addEventListener('click', click);
      button.nativeScrollTop = {cleanup() { window.removeEventListener('scroll', update); button.removeEventListener('click', click); delete button.nativeScrollTop; }};
      update();
    });
  }
  function initializeSourceCarousels(container) {
    container.querySelectorAll('[data-native-source-carousel]').forEach(carousel => {
      if (carousel.nativeSourceCarousel) return;
      const slider = carousel.querySelector('.splide[id]');
      const track = carousel.querySelector('.splide__track');
      const list = track.querySelector('.splide__list');
      const slides = [...list.querySelectorAll(':scope > .splide__slide')];
      if (!slides.length) return;
      const previous = carousel.querySelector('.splide__arrow--prev');
      const next = carousel.querySelector('.splide__arrow--next');
      const dots = [...carousel.querySelectorAll('.splide__pagination__page')];
      const controls = [...carousel.querySelectorAll('.splide__arrows,.splide__pagination')];
      const perPage = Number(carousel.dataset.slidesMobile || 1);
      const last = Math.max(0, slides.length - perPage);
      let index = 0, drag, suppressClick = false;
      const mobile = () => innerWidth < 750; // Source base.css switches the destroyed desktop grid at 750px.
      const select = value => {
        index = mobile() ? Math.max(0, Math.min(value, last)) : 0;
        carousel.dataset.nativeSourceIndex = index;
        ['splide--slide','splide--ltr','splide--draggable','is-active'].forEach(name => slider.classList.toggle(name, mobile()));
        slider.classList.toggle('is-overflow', mobile() && last > 0);
        ['splide__track--slide','splide__track--ltr','splide__track--draggable'].forEach(name => track.classList.toggle(name, mobile()));
        if (mobile()) slider.setAttribute('aria-roledescription', 'carousel');
        else slider.removeAttribute('aria-roledescription');
        controls.forEach(control => { control.hidden = !mobile(); });
        list.style.transform = mobile() ? 'translateX(' + -(slides[index].offsetLeft - slides[0].offsetLeft) + 'px)' : '';
        track.style.touchAction = mobile() ? 'pan-y' : '';
        previous.disabled = index === 0;
        next.disabled = index === last;
        dots.forEach((dot, position) => {
          dot.classList.toggle('is-active', position === index);
          dot.setAttribute('aria-selected', String(position === index));
          dot.tabIndex = position === index ? 0 : -1;
        });
        slides.forEach((slide, position) => {
          const visible = !mobile() || position >= index && position < index + perPage;
          slide.classList.toggle('is-active', mobile() && position === index);
          slide.classList.toggle('is-visible', mobile() && visible);
          if (mobile()) {
            slide.setAttribute('role', 'tabpanel');
            slide.setAttribute('aria-roledescription', 'slide');
            slide.setAttribute('aria-label', (position + 1) + ' of ' + slides.length);
            slide.setAttribute('aria-hidden', String(!visible));
          } else ['role','aria-roledescription','aria-label','aria-hidden'].forEach(name => slide.removeAttribute(name));
          slide.inert = !visible;
        });
      };
      previous.addEventListener('click', () => select(index - 1));
      next.addEventListener('click', () => select(index + 1));
      dots.forEach((dot, position) => dot.addEventListener('click', () => select(position)));
      track.addEventListener('pointerdown', event => {
        if (!mobile() || event.button > 0 || event.target.closest('button')) return;
        suppressClick = false;
        drag = {x:event.clientX, y:event.clientY};
      });
      track.addEventListener('pointermove', event => {
        if (!drag) return;
        const horizontal = Math.abs(event.clientX - drag.x);
        if (horizontal > 8 && horizontal > Math.abs(event.clientY - drag.y)) {
          suppressClick = true;
          track.setPointerCapture(event.pointerId);
        }
      });
      track.addEventListener('pointerup', event => {
        if (drag) {
          const delta = event.clientX - drag.x;
          if (Math.abs(delta) > 40 && Math.abs(delta) > Math.abs(event.clientY - drag.y)) {
            suppressClick = true;
            select(index + (delta < 0 ? 1 : -1));
          }
        }
        drag = null;
      });
      track.addEventListener('pointercancel', () => { drag = null; suppressClick = false; });
      track.addEventListener('click', event => {
        if (suppressClick) { event.preventDefault(); event.stopPropagation(); suppressClick = false; }
      }, true);
      track.querySelectorAll('img,a[href]').forEach(node => { node.draggable = false; });
      const observer = new ResizeObserver(() => select(index));
      observer.observe(track);
      carousel.nativeSourceCarousel = {select,cleanup:() => observer.disconnect()};
      select(index);
    });
  }
  function gpValue(carousel, key, fallback = false) {
    const value = carousel.nativeCarouselConfig?.[key] || {};
    return innerWidth <= 767 ? value.mobile ?? value.tablet ?? value.desktop ?? fallback : innerWidth <= 1024 ? value.tablet ?? value.desktop ?? fallback : value.desktop ?? fallback;
  }
  function updateGPCarousel(carousel, index) {
    const track = carousel.querySelector('[data-native-carousel-track]');
    const slides = [...track.querySelectorAll(':scope > [data-native-carousel-slide]')];
    if (!slides.length) return;
    if (index === undefined) index = slides.reduce((best, slide, current) => Math.abs(slide.offsetLeft - slides[0].offsetLeft - track.scrollLeft) < Math.abs(slides[best].offsetLeft - slides[0].offsetLeft - track.scrollLeft) ? current : best, 0);
    carousel.dataset.nativeCarouselIndex = index;
    carousel.setAttribute('active-index', index);
    const perView = Number(gpValue(carousel, 'items', 1)) || 1;
    const dotIndex = gpValue(carousel, 'loop') || gpValue(carousel, 'sneak') ? index : Math.ceil(index / perView);
      const dotContainer = carousel.querySelector('.gp-carousel-dot-container');
      const templateDot = dotContainer?.querySelector('.gp-carousel-dot');
      if (templateDot && gpValue(carousel, 'dot') && gpValue(carousel, 'dotStyle', 'none') !== 'none') {
        const count = gpValue(carousel, 'loop') || gpValue(carousel, 'sneak') ? slides.length : Math.ceil(slides.length / perView);
        if (dotContainer.querySelectorAll('.gp-carousel-dot').length !== count) {
          const dots = Array.from({length:count}, (_, position) => {
            const dot = templateDot.cloneNode(true);
            dot.id = templateDot.id.replace(/-\d+$/, '-' + position);
            dot.dataset.index = position;
            return dot;
          });
          dotContainer.replaceChildren(...dots);
        }
      }
    carousel.querySelectorAll('.gp-carousel-dot').forEach((dot, position) => {
      const active = position === dotIndex;
      dot.classList.toggle('gp-carousel-dot--active', active);
      dot.classList.toggle('gem-slider-dot-active', active);
      dot.classList.toggle('gem-slider-dot-inactive', !active);
      dot.setAttribute('aria-pressed', String(active));
      dot.setAttribute('aria-label', `Go to carousel page ${position + 1}`);
        if (gpValue(carousel, 'dot')) {
          const color = gpValue(carousel, active ? 'dotActiveColor' : 'dotColor');
          if (color) dot.style.backgroundColor = /^(?:bg|line|text)-\d+$/.test(color) ? `var(--g-c-${color}, ${color})` : color;
          dot.style.width = gpValue(carousel, 'dotSize', 12) + 'px';
        }
    });
    slides.forEach((slide, position) => {
      slide.classList.toggle('swiper-slide-active', position === index);
      if (carousel.dataset.nativeCarousel === 'thumbnails') {
        slide.dataset.outline = position === index ? 'active' : 'deactive';
        slide.setAttribute('aria-pressed', String(position === index));
      }
    });
    carousel.querySelectorAll('.gp-carousel-action-back,.gp-carousel-action-next').forEach(button => {
        const allFit = track.scrollWidth <= track.clientWidth + 1;
        const navigation = gpValue(carousel, 'navigation', true) && gpValue(carousel, 'navigationStyle', 'none') !== 'none';
        button.disabled = allFit || !gpValue(carousel, 'loop') && (button.matches('.gp-carousel-action-back') ? track.scrollLeft <= 1 : track.scrollLeft + track.clientWidth >= track.scrollWidth - 1);
        button.style.setProperty('display', navigation ? 'flex' : 'none', 'important');
        if (button.disabled && (allFit || carousel.nativeCarouselConfig.hideDisabled !== false)) button.style.setProperty('visibility', 'hidden', 'important');
        else button.style.removeProperty('visibility');
        button.dataset.hidden = String(button.disabled && (allFit || carousel.nativeCarouselConfig.hideDisabled !== false));
    });
  }
  function selectGPCarousel(carousel, index, behavior = 'smooth') {
    const track = carousel?.querySelector('[data-native-carousel-track]');
    if (!track) return;
    const slides = [...track.querySelectorAll(':scope > [data-native-carousel-slide]')];
    if (!slides.length) return;
    index = gpValue(carousel, 'loop') ? (index % slides.length + slides.length) % slides.length : Math.max(0, Math.min(index, slides.length - 1));
    track.scrollTo({left:slides[index].offsetLeft - slides[0].offsetLeft, behavior});
    updateGPCarousel(carousel, index);
    if (carousel.dataset.nativeCarousel === 'feature') {
      const thumbnails = carousel.closest('[data-native-product-root]')?.querySelector('[data-native-carousel="thumbnails"]');
      if (thumbnails) updateGPCarousel(thumbnails, index);
    }
  }
  function initializeLPProgress(container) {
    container.querySelectorAll('[data-lp-progress]').forEach(bar => {
      if (bar.nativeProgressInitialized) return;
      bar.nativeProgressInitialized = true;
      const host = bar.closest('.shopify-section');
      const place = () => {
        const announcement = document.querySelector('[data-lp-announce]');
        if (announcement && host) host.style.top = announcement.offsetHeight + 'px';
      };
      const update = () => {
        if (!bar.isConnected) {
          window.removeEventListener('scroll', update);
          window.removeEventListener('resize', resize);
          window.removeEventListener('load', place);
          return;
        }
        const doc = document.documentElement;
        const max = doc.scrollHeight - innerHeight;
        const percent = max > 0 ? (window.scrollY || doc.scrollTop) / max * 100 : 0;
        bar.style.width = Math.min(100, Math.max(0, percent)) + '%';
      };
      const resize = () => { place(); update(); };
      window.addEventListener('scroll', update, {passive:true});
      window.addEventListener('resize', resize);
      window.addEventListener('load', place);
      place(); update();
    });
  }
  function initializeGPStickies(container) {
    const target = [...document.querySelectorAll('[data-native-sticky-cart-target]')].find(node => !node.closest('gp-sticky'));
    const cartButton = target?.matches('.gp-product-button') ? target : target?.querySelector('.product-form__submit,button[name=add]');
    container.querySelectorAll('gp-sticky[data-native-gp-sticky]').forEach(sticky => {
      if (sticky.nativeStickyInitialized) return;
      sticky.nativeStickyInitialized = true;
      const observers = new Map(), states = new Map(), lastUpdates = new Map();
      const update = () => {
        if (!sticky.isConnected) {
          observers.forEach(observer => observer.disconnect());
          window.removeEventListener('resize', update);
          return;
        }
        const device = innerWidth <= 767 ? 'mobile' : innerWidth <= 1024 ? 'tablet' : 'desktop';
        if (sticky.getAttribute('data-native-sticky-display-' + device) !== 'after-first-cart-button' || !cartButton || observers.has(device)) return;
        const enabled = sticky.getAttribute('data-native-sticky-enabled-' + device) !== 'false';
        const observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            const now = performance.now();
            if (now - (lastUpdates.get(device) || 0) < 200) return;
            requestAnimationFrame(() => {
              let state = states.get(device) || 'none';
              if (entry.isIntersecting) state = 'none';
              else if (entry.boundingClientRect.top < 0 && enabled) state = 'block';
              if (states.get(device) !== state) {
                states.set(device, state);
                sticky.style.setProperty('--d' + (device === 'desktop' ? '' : '-' + device), state);
              }
              lastUpdates.set(device, now);
            });
          });
        }, {threshold:0.1,rootMargin:'-20px 0px 0px 0px'});
        observers.set(device, observer);
        observer.observe(cartButton);
      };
      window.addEventListener('resize', update, {passive:true});
      update();
    });
  }
  function initializeGPCarousels(container) {
    const carousels = [...container.querySelectorAll('[data-native-carousel]')].filter(carousel => !carousel.nativeCarouselConfig);
    // Feature galleries update their sibling thumbnails during their first selection.
    carousels.forEach(carousel => {
      carousel.nativeCarouselConfig = JSON.parse(carousel.querySelector('[data-native-carousel-config]').textContent);
    });
    carousels.forEach(carousel => {
      const track = carousel.querySelector('[data-native-carousel-track]');
      track.addEventListener('scroll', () => updateGPCarousel(carousel), {passive:true});
        new ResizeObserver(() => updateGPCarousel(carousel)).observe(track);
      let drag;
      track.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || !gpValue(carousel, 'drag') || event.target.closest('button,a,[role=button]')) return;
        drag = {x:event.clientX, left:track.scrollLeft};
        track.setPointerCapture(event.pointerId);
      });
      track.addEventListener('pointermove', event => { if (drag) track.scrollLeft = drag.left - event.clientX + drag.x; });
      track.addEventListener('pointerup', () => { drag = null; updateGPCarousel(carousel); });
      track.addEventListener('pointercancel', () => { drag = null; });
      let hovering = false;
      carousel.addEventListener('mouseenter', () => { hovering = true; });
      carousel.addEventListener('mouseleave', () => { hovering = false; });
      if (carousel.nativeCarouselConfig.autoplay && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const timer = setInterval(() => {
          if (!carousel.isConnected) { clearInterval(timer); return; }
          const box = carousel.getBoundingClientRect();
          if (box.bottom > 0 && box.top < innerHeight && (!hovering || !carousel.nativeCarouselConfig.pauseOnHover)) selectGPCarousel(carousel, Number(carousel.dataset.nativeCarouselIndex || 0) + 1);
        }, carousel.nativeCarouselConfig.delay);
      }
      selectGPCarousel(carousel, Number(carousel.dataset.nativeCarouselInitial || 0), 'instant');
    });
  }
  function selectImage(gallery, index) {
    const slides = [...gallery.querySelectorAll('.product__media-list > li')];
    if (!slides.length) return;
    index = Math.max(0, Math.min(index, slides.length - 1));
    gallery.dataset.activeSlide = index;
    slides.forEach((slide, position) => {
      slide.classList.toggle('is-active', position === index);
      slide.classList.toggle('native-gallery-slide-hidden', position !== index);
    });
    gallery.querySelectorAll('.thumbnail-list__item button').forEach((button,position) => {
      if (position === index) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
    gallery.querySelectorAll('[id^=GalleryViewer] button[name]').forEach(button => {
      button.disabled = button.name === 'previous' ? index === 0 : index === slides.length - 1;
    });
    const counter = gallery.querySelector('.slider-counter--current');
    if (counter) counter.textContent = index + 1;
  }
  function updateProduct(form) {
    const product = productFor(form);
    if (!product) return;
    const variants = selectedVariants(form, product);
    form.querySelectorAll('[data-unit]').forEach((row, index) => {
      const color = row.querySelector('[data-color]');
      const swatch = color?.parentElement.querySelector('.kaching-bundles__swatch');
      if (swatch) {
        const image = variants[index]?.featured_image?.src;
        swatch.style.setProperty('--kaching-swatch-image-url', image ? 'url(' + JSON.stringify(image) + ')' : 'none');
        swatch.style.setProperty('--native-swatch-color', !image && variants[index] ? ({black:'#121212',white:'#fff'}[color.value.toLowerCase()] || 'transparent') : 'transparent');
      }
    });
    const available = variants.length && variants.every(variant => variant?.available);
    form.closest('[data-native-product-root]').querySelectorAll('[data-native-availability]').forEach(host => {
      const shadow = host.shadowRoot;
      if (!shadow) return;
      shadow.querySelector('.text strong').textContent = available ? 'Available' : 'Unavailable';
      shadow.querySelector('.body').style.setProperty('--icon-color', available ? '#009112' : '#767676');
    });
    const button = form.querySelector('[name=add]') || form.elements?.namedItem('add');
    if (!button) return;
    button.disabled = !available;
    (form.querySelector('[data-button-label]') || button.querySelector('[data-button-label]')).textContent = available ? (form.dataset.nativeButtonLabel || 'Add to cart') : 'Unavailable';
    const variant = variants[0];
    if (variant) {
      (form.elements?.namedItem('id') || form.querySelector('[name=id]')).value = variant.id;
      const singlePrice = form.querySelector('[data-offer-price]');
      if (singlePrice) singlePrice.textContent = money(variant.price);
      const compare = form.querySelector('[data-offer-compare]');
      if (compare) { compare.textContent = money(variant.compare_at_price || 0); compare.hidden = !(variant.compare_at_price > variant.price); }
      const savings = form.querySelector('[data-offer-savings]');
      if (savings) savings.textContent = money(Math.max(0, (variant.compare_at_price || 0) - variant.price));
      const bundle = form.querySelector('[data-bundle-price]');
      if (bundle) bundle.textContent = money(variant.price * 2);
      const bundleCompare = form.querySelector('[data-bundle-compare]');
      if (bundleCompare) bundleCompare.textContent = money(variant.price * 3) + ' separately';
      const bundleSavings = form.querySelector('[data-bundle-savings]');
      if (bundleSavings) bundleSavings.textContent = money(variant.price);
      if (!form.hasAttribute('data-native-card')) {
        const url = new URL(location.href); url.searchParams.set('variant', variant.id); history.replaceState({}, '', url);
      }
    }
    const count = variants.length;
    form.querySelectorAll('[data-unit]').forEach(row => { row.hidden = Number(row.dataset.unit) > count; });
    form.querySelectorAll('.kaching-bundles__bar').forEach(bar => bar.classList.toggle('kaching-bundles__bar--selected', !!bar.querySelector('input:checked')));
    const options = form.querySelector('[data-native-options]');
    const slot = form.querySelector('.kaching-bundles__bar--selected [data-offer-slot]');
    if (options && slot && options.parentElement !== slot) slot.append(options);
  }
  document.addEventListener('change', async event => {
    const form = event.target.closest('.native-purchase') || (event.target.form?.matches('.native-purchase') ? event.target.form : null);
    if (form) updateProduct(form);
    const reviews = event.target.closest('[data-native-reviews]');
    if (reviews && event.target.matches('.jdgm-sort-dropdown,[data-review-filter-rating]')) {
      if (event.target.hasAttribute('data-review-filter-rating')) reviews.dataset.reviewRating = event.target.value;
      renderReviews(reviews, true);
    }
    if (event.target.matches('[data-cart-quantity]')) {
      const line = event.target.closest('[data-line-key]');
      if (event.target.value.trim() === '') { event.target.value = event.target.defaultValue; return; }
      const quantity = Number(event.target.value);
      if (!Number.isSafeInteger(quantity) || quantity < 0) return;
      await changeCart(line.dataset.lineKey, quantity);
    }
  });
  async function changeCart(key, quantity) {
    if (!Number.isSafeInteger(quantity) || quantity < 0) return;
    const dialog = document.getElementById('NativeCart');
    if (dialog.getAttribute('aria-busy') === 'true') return;
    const wasOpen = dialog.hasAttribute('open');
    const closeRevision = cartCloseRevision;
    const active = document.activeElement;
    const lineKey = dialog.contains(active) ? active.closest('[data-line-key]')?.dataset.lineKey : null;
    const focusSelector = active?.matches('[data-cart-quantity]') ? '[data-cart-quantity]' : active?.matches('[data-cart-remove]') ? '[data-cart-remove]'
      : active?.matches('[data-cart-step]') ? `[data-cart-step="${CSS.escape(active.dataset.cartStep)}"]` : null;
    dialog.setAttribute('aria-busy', 'true');
    const controls = [...dialog.querySelectorAll('form input,form button,button[form=NativeCartForm]')].filter(control => !control.disabled);
    controls.forEach(control => { control.disabled = true; });
    try {
      await cartRequest('cart/change.js', {id:key, quantity});
      if (wasOpen && closeRevision === cartCloseRevision) {
        const current = document.getElementById('NativeCart');
        openDialog(current);
        if (lineKey && focusSelector) current.querySelector(`[data-line-key="${CSS.escape(lineKey)}"] ${focusSelector}`)?.focus();
      }
      status('Cart updated.');
    }
    catch(error) { const target = document.querySelector('[data-cart-error]'); target.textContent = error.message; target.hidden = false; }
    finally { dialog.removeAttribute('aria-busy'); controls.forEach(control => { control.disabled = false; }); }
  }
  document.addEventListener('submit', async event => {
    const form = event.target.closest('.native-purchase');
    if (!form) return;
    event.preventDefault();
    if (form.getAttribute('aria-busy') === 'true') return;
    const variants = selectedVariants(form, productFor(form));
    const error = form.querySelector('[role=alert]');
    error.hidden = true;
    if (!variants.length || !variants.every(variant => variant?.available)) { error.textContent = 'Select available options.'; error.hidden = false; return; }
    const button = form.querySelector('[name=add]') || form.elements?.namedItem('add');
    form.setAttribute('aria-busy','true'); button.disabled = true;
    const spinner = form.querySelector('.loading-overlay__spinner') || button.querySelector('.loading-overlay__spinner');
    spinner.hidden = false;
    try {
      const items = variants.map(variant => ({id:variant.id, quantity:1}));
      if (!form.querySelectorAll('[data-unit]').length) {
        const quantity = form.elements?.namedItem('quantity') || form.querySelector('[name=quantity]');
        items[0].quantity = quantity ? Number(quantity.value) : 1;
      }
      await cartRequest('cart/add.js', {items});
      const offer = form.querySelector('[name=native_offer]:checked');
      const code = offer?.dataset.discount ?? (variants.length === 3 ? form.querySelector('[data-offer-code]')?.textContent.trim() : '');
      if (code) {
        const response = await fetch(root + 'cart.js', {cache:'no-store'});
        if (!response.ok) throw new Error('Offer could not be applied. Review the cart before checkout.');
        const cart = await response.json();
        const ownCodes = new Set([...form.querySelectorAll('[data-discount]')].map(input => input.dataset.discount));
        const codes = [...new Set([...(cart.discount_codes || []).filter(discount => discount.applicable).map(discount => discount.code).filter(existing => !ownCodes.has(existing)), code])];
        const discounted = await cartRequest('cart/update.js', {discount:codes.join(',')});
        if (!discounted.discount_codes?.some(discount => discount.code === code && discount.applicable)) throw new Error('Offer is not applicable to this cart. Review the cart before checkout.');
      }
      if (form.dataset?.nativeRedirect) {
        const destination = new URL(form.dataset.nativeRedirect, location.href);
        if (destination.origin !== location.origin || !destination.pathname.startsWith(root + 'products/')) throw new Error('Product destination is unavailable. Review the cart before continuing.');
        location.assign(destination.href);
      } else openDialog(document.getElementById('NativeCart'), button);
      status('Added to cart.');
    } catch(cause) { error.textContent = cause.message; error.hidden = false; }
    finally { form.removeAttribute('aria-busy'); spinner.hidden = true; updateProduct(form); }
  });
  document.addEventListener('click', async event => {
    const imageOpener = event.target.closest('media-gallery modal-opener.product__modal-opener');
    if (imageOpener && event.detail !== 0 && Number(imageOpener.closest('media-gallery').dataset.nativeSwipeUntil) > Date.now()) {
      event.preventDefault();
      return;
    }
    const gpThumbnail = event.target.closest('gp-lite-html5-embed .gp-thumbnail-video');
    if (gpThumbnail) {
      event.preventDefault();
      gpThumbnail.style.display = 'none';
      const video = gpThumbnail.closest('gp-lite-html5-embed').querySelector('video');
      if (video) playVideo(video);
      return;
    }
    const target = event.target.closest('button,a,modal-opener,[role=button]');
    if (!target) return;
    if (target.matches('[data-native-quantity-step]')) {
      const input = target.closest('[data-native-source-picker]')?.querySelector('[name=quantity]');
      if (input) {
        input.value = Math.max(1, (Number(input.value) || 1) + Number(target.dataset.nativeQuantityStep));
        input.dispatchEvent(new Event('change', {bubbles:true}));
      }
      return;
    }
    if (target.matches('.menu-drawer__close-menu-btn')) {
      event.preventDefault();
      closeMenuDrawer(target.closest('header-drawer,menu-drawer')?.querySelector(':scope > details'));
      return;
    }
    if (target.matches('.search-modal__close-button')) {
      const details = target.closest('details-modal > details');
      details.open = false; details.querySelector(':scope > summary')?.focus();
      return;
    }
    if (target.matches('.internal-video__play')) {
      event.preventDefault();
      const video = target.closest('internal-video').querySelector('video');
      if (video.paused) playVideo(video); else video.pause();
      return;
    }
    if (reviewClick(target)) { event.preventDefault(); return; }
    const carousel = target.closest('[data-native-carousel]');
    if (carousel && target.matches('.gp-carousel-action-back,.gp-carousel-action-next,.gp-carousel-dot,[data-native-carousel-slide][role=button]')) {
      event.preventDefault();
      if (target.matches('[data-native-carousel-slide]')) {
        const feature = carousel.closest('[data-native-product-root]')?.querySelector('[data-native-carousel="feature"]');
        selectGPCarousel(feature, Number(target.dataset.nativeCarouselSlide));
        updateGPCarousel(carousel, Number(target.dataset.nativeCarouselSlide));
      } else if (target.matches('.gp-carousel-dot')) {
        const factor = gpValue(carousel, 'loop') || gpValue(carousel, 'sneak') ? 1 : Number(gpValue(carousel, 'items', 1)) || 1;
        selectGPCarousel(carousel, Number(target.dataset.index) * factor);
      } else selectGPCarousel(carousel, Number(carousel.dataset.nativeCarouselIndex || 0) + (target.matches('.gp-carousel-action-next') ? 1 : -1));
      return;
    }
    if (target.matches('[data-open-cart],a[href$="/cart"]') && document.getElementById('NativeCart')) {
      event.preventDefault();
      if (target.getAttribute('aria-busy') === 'true') return;
      target.setAttribute('aria-busy', 'true');
      try {
        await refreshCart();
        openDialog(document.getElementById('NativeCart'), target);
      } catch(error) { openDialog(document.getElementById('NativeCart'), target); const alert = document.querySelector('[data-cart-error]'); alert.textContent = error.message; alert.hidden = false; }
      finally { target.removeAttribute('aria-busy'); }
    } else if (target.matches('[data-close-dialog],.product-popup-modal__toggle')) {
      closeDialog(target.closest('dialog,modal-dialog'));
    } else if (target.matches('[data-cart-step]')) {
      const line = target.closest('[data-line-key]');
      const input = line.querySelector('[data-cart-quantity]');
      if (input.value.trim() === '') { input.value = input.defaultValue; return; }
      const quantity = Number(input.value);
      const step = Number(target.dataset.cartStep);
      if ((step !== -1 && step !== 1) || !Number.isSafeInteger(quantity) || quantity < 0) return;
      changeCart(line.dataset.lineKey, quantity + step);
    } else if (target.matches('[data-cart-remove]')) {
      changeCart(target.closest('[data-line-key]').dataset.lineKey, 0);
    } else if (target.closest('modal-opener[data-modal]')) {
      const opener = target.closest('modal-opener');
      const dialog = document.querySelector(opener.dataset.modal);
      if (dialog) { event.preventDefault(); openDialog(dialog, target); }
      else if (target.closest('media-gallery')) {
        const image = target.closest('.product__media-item')?.querySelector('img');
        openImageZoom(image, opener);
      }
    } else if (target.closest('.thumbnail-list__item')) {
      const gallery = target.closest('media-gallery');
      selectImage(gallery, [...gallery.querySelectorAll('.thumbnail-list__item')].indexOf(target.closest('.thumbnail-list__item')));
    } else if (target.closest('media-gallery') && target.matches('[name=next],[name=previous]')) {
      const gallery = target.closest('media-gallery');
      const thumbnails = target.closest('[id^=GalleryThumbnails]');
      if (thumbnails) {
        const strip = thumbnails.querySelector('.thumbnail-list');
        strip.scrollBy({left:strip.clientWidth * (target.name === 'next' ? 1 : -1), behavior:'smooth'});
      } else selectImage(gallery, Number(gallery.dataset.activeSlide || 0) + (target.name === 'next' ? 1 : -1));
    } else if (target.closest('modal-opener.product__modal-opener')) {
      const image = target.closest('.product__media-item').querySelector('img');
      openImageZoom(image, target.closest('modal-opener.product__modal-opener'));
    }
  });
  document.addEventListener('keydown', event => {
    if ((event.key === 'Enter' || event.key === ' ') && event.target.matches('[data-native-reviews] [role=button],[data-native-carousel-slide][role=button],media-gallery modal-opener.product__modal-opener[role=button]')) {
      event.preventDefault(); event.target.click();
    }
    const dialog = document.querySelector('dialog[open],modal-dialog[open]');
    const search = document.querySelector('details-modal > details[open]');
    const menu = document.querySelector('menu-drawer > details[open],header-drawer > details[open]');
    const panel = dialog || search || menu;
    if (!panel) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      if (dialog) closeDialog(dialog);
      else if (menu && panel === menu) closeMenuDrawer(menu);
      else { panel.open = false; panel.querySelector('summary')?.focus(); }
    }
    if (event.key === 'Tab' && panel.tagName !== 'DIALOG') {
      const focusPanel = panel === search ? search.querySelector('.search-modal') : panel;
      const nodes = [...focusPanel.querySelectorAll('summary,button,a[href],input,select,textarea,[tabindex="0"]')].filter(node => !node.disabled && node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden');
      if (!nodes.length) { event.preventDefault(); return; }
      if (event.shiftKey && document.activeElement === nodes[0]) { event.preventDefault(); nodes.at(-1).focus(); }
      else if (!event.shiftKey && document.activeElement === nodes.at(-1)) { event.preventDefault(); nodes[0].focus(); }
    }
  });
  document.addEventListener('click', event => {
    if (event.target.matches('dialog,modal-dialog[open]')) {
      if (event.target.id === 'NativeCart') {
        const bounds = event.target.getBoundingClientRect();
        if (event.clientX >= bounds.left && event.clientX <= bounds.right && event.clientY >= bounds.top && event.clientY <= bounds.bottom) return;
      }
      closeDialog(event.target);
    }
    else if (event.target.matches('details-modal .modal-overlay')) {
      const details = event.target.closest('details');
      details.open = false; details.querySelector(':scope > summary')?.focus();
    }
  });
  let pointerStart;
  document.addEventListener('pointerdown', event => {
    const gallery = event.target.closest('media-gallery');
    if (gallery && event.target.closest('.product__media-list') && (event.button ?? 0) === 0) pointerStart = {gallery,x:event.clientX,y:event.clientY};
  });
  document.addEventListener('pointerup', event => {
    if (!pointerStart) return;
    const delta = event.clientX-pointerStart.x;
    if (!event.target.closest('[id^=GalleryThumbnails]') && Math.abs(delta)>40 && Math.abs(delta)>Math.abs(event.clientY-pointerStart.y)) {
      pointerStart.gallery.dataset.nativeSwipeUntil = Date.now() + 350;
      selectImage(pointerStart.gallery,Number(pointerStart.gallery.dataset.activeSlide||0)+(delta<0?1:-1));
    }
    pointerStart = null;
  });
  document.addEventListener('pointercancel', () => { pointerStart = null; });
  document.addEventListener('close', event => {
    if (event.target.id === 'NativeCart') {
      if (event.target.isConnected && !event.target.hasAttribute('open') && cartOverflow !== undefined) closeDialog(event.target);
      return;
    }
    if (event.target.tagName === 'DIALOG') { document.body.style.overflow = ''; restoreFocus?.focus(); }
  }, true);
  document.addEventListener('toggle', event => {
    const details = event.target;
    if (details.tagName !== 'DETAILS') return;
    details.querySelector(':scope > summary')?.setAttribute('aria-expanded', String(details.open));
    if (details.parentElement.matches('details-modal')) {
      document.body.style.overflow = details.open || document.querySelector('dialog[open],modal-dialog[open],menu-drawer > details[open],header-drawer > details[open]') ? 'hidden' : '';
      if (details.open) details.querySelector('input[type=search]')?.focus();
      else details.querySelector(':scope > summary')?.focus();
    }
    if (details.closest('menu-drawer,header-drawer')) {
      details.classList.toggle('menu-opening', details.open);
      if (details.parentElement.matches('menu-drawer,header-drawer')) {
        details.closest('.section-header')?.classList.toggle('menu-open', details.open);
        document.body.style.overflow = details.open || document.querySelector('dialog[open],modal-dialog[open],details-modal > details[open]') ? 'hidden' : '';
      }
    }
  }, true);
  function initializeFeedback(container) {
    container.querySelectorAll('[data-native-feedback]').forEach(form => {
      if (form.dataset.feedbackInitialized) return;
      form.dataset.feedbackInitialized = 'true';
      const stars = [...form.querySelectorAll('#stars .star[data-n]')];
      const rating = form.querySelector('[data-feedback-rating]');
      const hint = form.querySelector('#hint');
      const labels = ['', 'Very poor', 'Poor', 'Average', 'Great', 'Excellent'];
      const colors = ['', '#FF3722', '#FF8622', '#FFCE00', '#73CF11', '#00B67A'];
      const paint = value => {
        stars.forEach(star => {
          star.style.background = Number(star.dataset.n) <= value ? colors[value] : '#DCDCE6';
          star.setAttribute('aria-pressed', String(Number(star.dataset.n) === Number(rating.value)));
        });
        hint.textContent = labels[value] || '\u00a0';
      };
      const show = id => form.querySelectorAll('.step').forEach(step => step.classList.toggle('active', step.id === id));
      const choose = star => {
        rating.value = star.dataset.n;
        paint(Number(rating.value));
        show('step2');
        form.querySelector('#fName').focus();
      };
      stars.forEach(star => {
        star.addEventListener('mouseenter', () => paint(Number(star.dataset.n)));
        star.addEventListener('mouseleave', () => paint(Number(rating.value)));
        star.addEventListener('focus', () => paint(Number(star.dataset.n)));
        star.addEventListener('blur', () => paint(Number(rating.value)));
        star.addEventListener('click', () => choose(star));
        star.addEventListener('keydown', event => {
          if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); choose(star); }
        });
      });
      form.querySelector('[data-feedback-back]').addEventListener('click', () => {
        show('step1');
        (stars.find(star => star.dataset.n === rating.value) || stars[0]).focus();
      });
      paint(Number(rating.value));
    });
  }
  function initialize(container = document) {
    initializeFacetFilters(container);
    initializeFeedback(container);
    initializeHeaders(container);
    initializeVideos(container);
    initializeLandingGalleries(container);
    initializeScrollTop(container);
    initializeSourceCarousels(container);
    initializeGPCarousels(container);
    initializeLPProgress(container);
    initializeGPStickies(container);
    initializePredictiveSearch(container);
    container.querySelectorAll('#NativeWriteReview').forEach(dialog => {
      const form = dialog.querySelector('form');
      if (!form || form.nativeReviewInitialized) return;
      form.nativeReviewInitialized = true;
      form.addEventListener('submit', event => {
        const id = form.querySelector('[data-native-review-product-id]')?.value || '';
        const title = form.querySelector('[data-native-review-product]')?.value?.trim();
        if (!/^\d+$/.test(id) || !Number.isSafeInteger(Number(id)) || Number(id) <= 0 || !title) {
          event.preventDefault();
          status('Select the product using its Write a review button before sending.');
        }
      });
      if (dialog.querySelector('[data-native-review-result]')) {
        const trigger = document.querySelector('[data-native-reviews] button[data-native-write-review]');
        if (trigger) openReviewForm(trigger);
      }
    });
    updateCartCount(document.getElementById('NativeCartSection'));
    container.querySelectorAll('[data-destination-social]').forEach(link => { if (!link.getAttribute('href')) link.hidden = true; });
    container.querySelectorAll('media-gallery').forEach(gallery => {
      selectImage(gallery, Number(gallery.dataset.activeSlide || 0));
      gallery.querySelectorAll('[id^=GalleryThumbnails] .thumbnail-list').forEach(strip => {
        const update = () => strip.closest('slider-component').querySelectorAll('button[name]').forEach(button => {
          button.disabled = button.name === 'previous' ? strip.scrollLeft <= 1 : strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 1;
        });
        strip.addEventListener('scroll', update, {passive:true}); update();
      });
    });
    container.querySelectorAll('.native-purchase').forEach(form => updateProduct(form));
    container.querySelectorAll('[data-native-reviews]').forEach(widget => {
      widget.dataset.initialReviewLimit = widget.dataset.reviewLimit || '4';
      renderReviews(widget);
      widget.querySelectorAll('.jm-media-grid__image').forEach(image => {
        const loaded = () => {
          image.classList.add('jm-media-grid__image--loaded');
          image.closest('.jm-media-grid__item')?.classList.remove('jm-media-grid__item--loading');
          const spinner = image.parentElement.querySelector('[aria-label="Loading thumbnail"]');
          if (spinner) spinner.hidden = true;
        };
        image.addEventListener('load', loaded);
        if (image.complete && image.naturalWidth) loaded();
      });
    });
  }
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [host, cleanup] of headerControllers) if (event.target.contains(host)) cleanup();
    event.target.querySelectorAll('[data-native-source-carousel]').forEach(carousel => carousel.nativeSourceCarousel?.cleanup());
    event.target.querySelectorAll('.lps-buy-media').forEach(gallery => gallery.nativeLandingGallery?.cleanup());
    event.target.querySelectorAll('[data-native-scroll-top]').forEach(button => button.nativeScrollTop?.cleanup());
    event.target.querySelectorAll('predictive-search').forEach(host => host.nativeSearch?.cleanup());
  });
  window.addEventListener('pageshow', async () => {
    const wasOpen = document.getElementById('NativeCart')?.hasAttribute('open');
    const closeRevision = cartCloseRevision;
    try {
      await refreshCart();
      if (wasOpen && closeRevision === cartCloseRevision) openDialog(document.getElementById('NativeCart'));
    } catch(error) { status(error.message + ' Open the cart to retry.'); }
  });
  initialize();
})();
